import React, { createContext, useContext, useState, useEffect } from 'react';

const DarkModeContext = createContext();

export const DarkModeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check for user preference in localStorage first
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      console.log('🌙 DarkMode: Using saved preference:', JSON.parse(savedMode));
      return JSON.parse(savedMode);
    }
    // If no saved preference, check system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      console.log('🌙 DarkMode: Following system preference:', systemPrefersDark);
      return systemPrefersDark;
    }
    // Fallback to light mode
    console.log('🌙 DarkMode: Fallback to light mode');
    return false;
  });

  const [isFollowingSystem, setIsFollowingSystem] = useState(() => {
    return localStorage.getItem('darkMode') === null;
  });

  useEffect(() => {
    // Apply or remove 'dark' class on the document body
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Only save to localStorage if user has manually set a preference
    if (!isFollowingSystem) {
      localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    }
  }, [isDarkMode, isFollowingSystem]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      console.log('🌙 DarkMode: System preference changed to:', e.matches);
      // Only update if user is following system preference
      if (isFollowingSystem) {
        console.log('🌙 DarkMode: Updating to follow system preference');
        setIsDarkMode(e.matches);
      } else {
        console.log('🌙 DarkMode: Ignoring system change - user has manual preference');
      }
    };

    // Listen for changes
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [isFollowingSystem]);

  const toggleDarkMode = () => {
    setIsDarkMode(prevMode => !prevMode);
    setIsFollowingSystem(false); // User is now manually setting preference
  };

  const resetToSystemPreference = () => {
    // Remove saved preference and follow system
    localStorage.removeItem('darkMode');
    setIsFollowingSystem(true);
    if (typeof window !== 'undefined' && window.matchMedia) {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(systemPrefersDark);
    } else {
      setIsDarkMode(false); // Fallback to light mode
    }
  };

  return (
    <DarkModeContext.Provider value={{ 
      isDarkMode, 
      toggleDarkMode, 
      resetToSystemPreference,
      isFollowingSystem 
    }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => useContext(DarkModeContext);
